/**
 * component-nav.js — Navigation Web Components for Mirai Online
 *
 * Contains three custom elements (no modules/imports — vanilla JS only):
 *  1. StickyHeader  — IntersectionObserver-based sticky behavior
 *  2. MegaMenu      — Hover (desktop) / click (touch) dropdown panels
 *  3. MobileNav     — Left-sliding panel with focus trap and accordion
 *
 * Security: No innerHTML with user-provided content (T-01-06).
 * Dispatches: 'nav:open' and 'nav:close' custom events on document.
 */

/* ============================================================
   1. STICKY HEADER
   ============================================================ */

class StickyHeader extends HTMLElement {
  // Scroll past this (px) → compact "Three Way" state (upper portion collapses,
  // brand logo drops into the middle slot, replacing the image logo).
  //
  // Two thresholds (hysteresis) instead of one: enter the compact state at
  // ENTER, leave it only after scrolling back below EXIT. The dead-band
  // between them prevents flicker when scrollY hovers at the boundary.
  //
  // CRITICAL: the dead-band (ENTER − EXIT) MUST exceed the header's collapse
  // height. Entering compact shrinks .header__top by --header-top-height (48px)
  // — and because #header-group is position:sticky, that 48px reduction at the
  // top of the document makes the browser's scroll anchoring yank scrollY down
  // by ~48px. If that kick is larger than the dead-band, scrollY lands back
  // across the opposite threshold and the state oscillates (the flicker bug).
  // With a 48px collapse we need ENTER − EXIT > 48, with margin for momentum
  // overshoot. 90/10 → a 80px dead-band that comfortably absorbs the kick.
  static SCROLL_ENTER = 90;
  static SCROLL_EXIT = 10;

  constructor() {
    super();
    this._ticking = false;
    this._boundScroll = this._handleScroll.bind(this);
  }

  connectedCallback() {
    if (this.hasAttribute('data-sticky-disabled')) return;

    // Set initial state (handles reloads / deep links that restore scroll).
    this._update();

    window.addEventListener('scroll', this._boundScroll, { passive: true });

    // Theme editor section reload compatibility
    this.addEventListener('section:load', () => {
      this.disconnectedCallback();
      this.connectedCallback();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._boundScroll);
  }

  _handleScroll() {
    // rAF-throttle: at most one class update per frame.
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._update();
      this._ticking = false;
    });
  }

  _update() {
    const y = window.scrollY;
    const isScrolled = this.classList.contains('header--scrolled');

    // Hysteresis: only flip state when crossing the *far* threshold, so the
    // dead-band between EXIT and ENTER absorbs jitter at the boundary.
    let scrolled = isScrolled;
    if (!isScrolled && y > StickyHeader.SCROLL_ENTER) {
      scrolled = true;
    } else if (isScrolled && y < StickyHeader.SCROLL_EXIT) {
      scrolled = false;
    }

    this.classList.toggle('header--scrolled', scrolled);
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/* ============================================================
   2. MEGA MENU
   ============================================================ */

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    // Bound method references for proper cleanup
    this._openTimers = new Map();  // trigger handle -> timeout ID
    this._closeTimers = new Map(); // trigger handle -> timeout ID
    this._activePanelHandle = null;

    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundScrimClick = this._handleScrimClick.bind(this);
    this._boundFocusOut = this._handleFocusOut.bind(this);
  }

  connectedCallback() {
    this._scrim = this.querySelector('[data-mega-menu-scrim]')
      || document.querySelector('[data-mega-menu-scrim]');

    this._triggers = Array.from(
      this.querySelectorAll('[data-mega-menu-trigger]')
    );

    this._triggers.forEach(trigger => {
      const handle = trigger.dataset.megaMenuTrigger;
      const panel = document.getElementById('mega-menu-' + handle);
      if (!panel) return;

      const isTouch = window.matchMedia('(hover: none)').matches;

      // Click toggle is bound on EVERY device, not just touch. The trigger is a
      // <button aria-haspopup>, so this is also the keyboard path: Enter/Space
      // fire a click. Binding it only on the touch branch left desktop keyboard
      // users with no way to open the menu at all.
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (this._activePanelHandle === handle) {
          this._closePanel(handle);
        } else {
          this._openPanel(handle);
        }
      });

      if (!isTouch) {
        // Desktop: hover with delays (D-02)
        trigger.addEventListener('mouseenter', () => {
          this._clearClose(handle);
          this._openTimers.set(handle, setTimeout(() => {
            this._openPanel(handle);
          }, 200));
        });

        trigger.addEventListener('mouseleave', () => {
          this._clearOpen(handle);
          this._closeTimers.set(handle, setTimeout(() => {
            this._closePanel(handle);
          }, 300));
        });

        panel.addEventListener('mouseenter', () => {
          this._clearClose(handle);
        });

        panel.addEventListener('mouseleave', () => {
          this._clearClose(handle);
          this._closeTimers.set(handle, setTimeout(() => {
            this._closePanel(handle);
          }, 300));
        });
      }
    });

    document.addEventListener('keydown', this._boundKeydown);

    // Tabbing out of the trigger/panel must close the menu, or the panel is
    // left open behind the shopper as focus walks on into the rest of the header.
    this.addEventListener('focusout', this._boundFocusOut);

    if (this._scrim) {
      this._scrim.addEventListener('click', this._boundScrimClick);
    }

    // Theme editor section reload compatibility
    this.addEventListener('section:load', () => {
      this.disconnectedCallback();
      this.connectedCallback();
    });
  }

  disconnectedCallback() {
    // Clear all pending timers
    this._openTimers.forEach(id => clearTimeout(id));
    this._closeTimers.forEach(id => clearTimeout(id));
    this._openTimers.clear();
    this._closeTimers.clear();

    document.removeEventListener('keydown', this._boundKeydown);
    this.removeEventListener('focusout', this._boundFocusOut);

    if (this._scrim) {
      this._scrim.removeEventListener('click', this._boundScrimClick);
    }
  }

  _handleFocusOut(e) {
    if (!this._activePanelHandle) return;

    const handle = this._activePanelHandle;
    const panel = document.getElementById('mega-menu-' + handle);
    const trigger = this._triggers.find((t) => t.dataset.megaMenuTrigger === handle);
    const next = e.relatedTarget;

    // No relatedTarget means focus left the document (alt-tab) — leave it open.
    if (!next) return;
    if (trigger === next) return;
    if (panel && panel.contains(next)) return;

    this._closePanel(handle);
  }

  _clearOpen(handle) {
    if (this._openTimers.has(handle)) {
      clearTimeout(this._openTimers.get(handle));
      this._openTimers.delete(handle);
    }
  }

  _clearClose(handle) {
    if (this._closeTimers.has(handle)) {
      clearTimeout(this._closeTimers.get(handle));
      this._closeTimers.delete(handle);
    }
  }

  _openPanel(handle) {
    // Close any currently open panel first
    if (this._activePanelHandle && this._activePanelHandle !== handle) {
      this._closePanel(this._activePanelHandle, true);
    }

    const panel = document.getElementById('mega-menu-' + handle);
    const trigger = this._triggers.find(t => t.dataset.megaMenuTrigger === handle);
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    if (this._scrim) {
      this._scrim.classList.add('is-visible');
      this._scrim.setAttribute('aria-hidden', 'false');
    }

    this._activePanelHandle = handle;
  }

  _closePanel(handle, immediate) {
    const panel = document.getElementById('mega-menu-' + handle);
    const trigger = this._triggers.find(t => t.dataset.megaMenuTrigger === handle);
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('is-open');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (this._scrim) {
      this._scrim.classList.remove('is-visible');
      this._scrim.setAttribute('aria-hidden', 'true');
    }

    if (this._activePanelHandle === handle) {
      this._activePanelHandle = null;
    }
  }

  _handleKeydown(e) {
    if (e.key !== 'Escape' || !this._activePanelHandle) return;

    const handle = this._activePanelHandle;
    const trigger = this._triggers.find((t) => t.dataset.megaMenuTrigger === handle);
    this._closePanel(handle);

    // Return focus to the trigger, otherwise focus is stranded inside the panel
    // we just hid and the next Tab restarts from the top of the document.
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
  }

  _handleScrimClick() {
    if (this._activePanelHandle) {
      this._closePanel(this._activePanelHandle);
    }
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

/* ============================================================
   3. MOBILE NAV
   ============================================================ */

class MobileNav extends HTMLElement {
  constructor() {
    super();
    this._boundKeydown = this._handleKeydown.bind(this);
    this._boundTriggerClick = this._handleTriggerClick.bind(this);
    this._boundOverlayClick = this._handleOverlayClick.bind(this);
    this._releaseFocusTrap = null;
    this._triggerElement = null;
  }

  connectedCallback() {
    this._closeBtn = this.querySelector('[data-mobile-nav-close]');
    this._overlay = document.querySelector('[data-mobile-nav-overlay]');

    // The hamburger lives in the HEADER section; <mobile-nav> lives in the layout.
    // Caching the node meant that any header edit in the theme editor replaced the
    // button and left this pointing at a removed node — the hamburger went dead.
    // Delegating from document survives any header re-render.
    document.addEventListener('click', this._boundTriggerClick);

    if (this._closeBtn) {
      // Named handler, not an anonymous arrow: disconnectedCallback never removed
      // this, so a re-init double-bound it and one tap toggled open-then-closed.
      this._closeBtn.addEventListener('click', this._boundCloseClick = () => this.close());
    }

    if (this._overlay) {
      this._overlay.addEventListener('click', this._boundOverlayClick);
    }

    document.addEventListener('keydown', this._boundKeydown);

    // Close if a resize/rotate carries us into the desktop breakpoint, where the
    // hamburger is display:none — the drawer would otherwise stay open over a
    // desktop header that is still `inert`, with the page still scroll-locked.
    this._mql = window.matchMedia('(min-width: 750px)');
    this._boundMqlChange = (e) => {
      if (e.matches && this.getAttribute('aria-hidden') === 'false') this.close();
    };
    this._mql.addEventListener('change', this._boundMqlChange);

    // Set up accordion for level-2 submenus
    this._setupAccordion();

    // Theme editor section reload compatibility
    this.addEventListener('section:load', () => {
      this.disconnectedCallback();
      this.connectedCallback();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundTriggerClick);
    document.removeEventListener('keydown', this._boundKeydown);

    if (this._closeBtn && this._boundCloseClick) {
      this._closeBtn.removeEventListener('click', this._boundCloseClick);
    }

    if (this._overlay) {
      this._overlay.removeEventListener('click', this._boundOverlayClick);
    }

    if (this._mql && this._boundMqlChange) {
      this._mql.removeEventListener('change', this._boundMqlChange);
      this._mql = null;
    }

    // Accordion handlers were never removed, so a re-init double-bound every one
    // of them and a single tap fired the toggle twice (open, then immediately closed).
    if (this._accordionCleanup) {
      this._accordionCleanup.forEach((fn) => fn());
      this._accordionCleanup = null;
    }

    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }
  }

  _setupAccordion() {
    const accordionTriggers = this.querySelectorAll('[data-mobile-nav-accordion-trigger]');
    this._accordionCleanup = [];

    accordionTriggers.forEach(trigger => {
      const handler = () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const controlsId = trigger.getAttribute('aria-controls');
        const submenu = this.querySelector('#' + controlsId);

        // Close all other open submenus (one open at a time)
        accordionTriggers.forEach(otherTrigger => {
          if (otherTrigger !== trigger) {
            const otherControlsId = otherTrigger.getAttribute('aria-controls');
            const otherSubmenu = this.querySelector('#' + otherControlsId);
            otherTrigger.setAttribute('aria-expanded', 'false');
            if (otherSubmenu) otherSubmenu.hidden = true;
          }
        });

        // Toggle current
        trigger.setAttribute('aria-expanded', String(!isExpanded));
        if (submenu) {
          submenu.hidden = isExpanded;
        }
      };

      trigger.addEventListener('click', handler);
      this._accordionCleanup.push(() => trigger.removeEventListener('click', handler));
    });
  }

  _handleTriggerClick(e) {
    // Delegated: resolve the hamburger from the event target, since the listener
    // is on document rather than on the (re-renderable) button itself.
    const trigger = e.target.closest('[data-mobile-nav-trigger]');
    if (!trigger) return;
    this._triggerElement = trigger;
    this.open();
  }

  /** Always resolve the hamburger fresh — never hold a reference across renders. */
  _currentTrigger() {
    return document.querySelector('[data-mobile-nav-trigger]') || {};
  }

  _handleKeydown(e) {
    if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
      this.close();
    }
  }

  _handleOverlayClick() {
    this.close();
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    window.Theme.lockScroll();

    // Apply inert to background content for accessibility
    const main = document.querySelector('#MainContent');
    const headerGroup = document.querySelector('#header-group');
    if (main) main.setAttribute('inert', '');
    if (headerGroup) headerGroup.setAttribute('inert', '');

    // Show overlay
    if (this._overlay) {
      this._overlay.classList.add('is-visible');
    }

    // Query the trigger live — it is no longer cached (the header section can be
    // re-rendered out from under us), so a stale reference would silently leave
    // aria-expanded wrong forever.
    this._currentTrigger().setAttribute?.('aria-expanded', 'true');

    // Dispatch nav:open event
    document.dispatchEvent(new CustomEvent('nav:open', {
      bubbles: true,
      detail: { panel: this }
    }));

    // Activate focus trap (re-queries on each Tab keypress — avoids stale references)
    this._releaseFocusTrap = this._trapFocus();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    window.Theme.unlockScroll();

    // Remove inert from background content
    const main = document.querySelector('#MainContent');
    const headerGroup = document.querySelector('#header-group');
    if (main) main.removeAttribute('inert');
    if (headerGroup) headerGroup.removeAttribute('inert');

    // Hide overlay
    if (this._overlay) {
      this._overlay.classList.remove('is-visible');
    }

    this._currentTrigger().setAttribute?.('aria-expanded', 'false');

    // Dispatch nav:close event
    document.dispatchEvent(new CustomEvent('nav:close', {
      bubbles: true,
      detail: { panel: this }
    }));

    // Release focus trap
    if (this._releaseFocusTrap) {
      this._releaseFocusTrap();
      this._releaseFocusTrap = null;
    }

    // Return focus to the trigger element that opened the nav
    if (this._triggerElement) {
      this._triggerElement.focus();
      this._triggerElement = null;
    }
  }

  _trapFocus() {
    const FOCUSABLE_SELECTORS = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    // Re-query on each Tab press to handle dynamic content (accordion open/close)
    const getFocusable = () => {
      return Array.from(this.querySelectorAll(FOCUSABLE_SELECTORS)).filter(el => {
        return !el.closest('[hidden]') && !el.hasAttribute('inert');
      });
    };

    const handleKeydown = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    this.addEventListener('keydown', handleKeydown);

    // Set initial focus on the close button
    const closeBtn = this.querySelector('[data-mobile-nav-close]');
    if (closeBtn) {
      closeBtn.focus();
    } else {
      const focusable = getFocusable();
      if (focusable.length > 0) focusable[0].focus();
    }

    return () => this.removeEventListener('keydown', handleKeydown);
  }
}

if (!customElements.get('mobile-nav')) {
  customElements.define('mobile-nav', MobileNav);
}
